/**
 * Idle-aware CRM presence + unified session log.
 *
 * Records both strict active minutes (input ≤30s) and presence minutes
 * (visible+focused, input ≤180s, mousemove counts). Always writes
 * crmActivitySessions with entity attribution (no skip-on-job-timer).
 * Retry queue + health counters on write failure.
 *
 * Desktop browser only by product decision — phone use is not tracked
 * specially and background tabs never count.
 */
import { useEffect, useRef } from 'react';
import {
  classifyActivitySegment,
} from '../utils/crmActivityHours';
import { incrementCrmActivity } from '../utils/crmActivityService';
import {
  type CrmActivityContext,
  crmActivitySessionKey,
} from '../utils/crmActivityContext';
import { saveCrmActivitySession } from '../utils/crmActivitySessionService';
import { getCrmActivityTabId } from '../utils/crmActivityTabId';
import { getCrmActivitySessionId } from '../utils/crmActivitySessionId';
import { startCrmActivitySessionWriteQueuePump } from '../utils/crmActivitySessionWriteQueue';
import type { CrmActivityAttributedTo } from '../types';
import {
  isWindowFocused,
  useIdleAwareTimeSegments,
} from './useIdleAwareTimeSegments';

const SYNC_INTERVAL_MS = 5 * 1000;

interface UseCrmActivityTrackingOptions {
  userId?: string;
  userName?: string;
  userEmail?: string;
  timezone: string;
  enabled?: boolean;
  context: CrmActivityContext;
  /** @deprecated Unified recorder always writes; kept for call-site BC. */
  skipSessionWrite?: boolean;
  projectTrackingActive?: boolean;
  leadTrackingActive?: boolean;
}

export function useCrmActivityTracking({
  userId,
  userName,
  userEmail,
  timezone,
  enabled = true,
  context,
  skipSessionWrite = false,
  projectTrackingActive = false,
  leadTrackingActive = false,
}: UseCrmActivityTrackingOptions) {
  const userIdRef = useRef(userId);
  const userNameRef = useRef(userName);
  const userEmailRef = useRef(userEmail);
  const timezoneRef = useRef(timezone);
  const contextRef = useRef(context);
  const skipRef = useRef(skipSessionWrite);
  const projectActiveRef = useRef(projectTrackingActive);
  const leadActiveRef = useRef(leadTrackingActive);
  // Carry sub-second / short remainders into the next flush of the same key.
  const carryMinutesRef = useRef(0);
  const carryKeyRef = useRef<string | null>(null);
  userIdRef.current = userId;
  userNameRef.current = userName;
  userEmailRef.current = userEmail;
  timezoneRef.current = timezone;

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    return startCrmActivitySessionWriteQueuePump();
  }, [enabled, userId]);

  useIdleAwareTimeSegments({
    enabled: Boolean(enabled && userId),
    sessionKey: crmActivitySessionKey(context, false),
    tickIntervalMs: SYNC_INTERVAL_MS,
    dualMeasures: true,
    closeAtLastActivityPlusThreshold: true,
    onSegment: async ({ start, end, minutes, activeSeconds, presenceSeconds, sessionKey }) => {
      const uid = userIdRef.current;
      if (!uid) return;

      const key = typeof sessionKey === 'string' ? sessionKey : '';
      if (carryKeyRef.current && carryKeyRef.current !== key) {
        carryMinutesRef.current = 0;
      }
      carryKeyRef.current = key;

      let activeMinutes = Math.max(0, activeSeconds) / 60 + carryMinutesRef.current;
      const presenceMinutes = Math.max(0, presenceSeconds) / 60;
      // Prefer explicit activeSeconds; fall back to minutes for BC.
      if (activeSeconds <= 0 && minutes > 0) activeMinutes = minutes + carryMinutesRef.current;

      // Carry tiny remainders (<1s) into the next segment of the same key.
      if (activeMinutes > 0 && activeMinutes < 1 / 60 && presenceMinutes < 1 / 60) {
        carryMinutesRef.current = activeMinutes;
        return;
      }
      carryMinutesRef.current = 0;

      const slices = classifyActivitySegment(start, end, timezoneRef.current);
      if (slices.length > 0) {
        try {
          await incrementCrmActivity(uid, timezoneRef.current, slices);
        } catch (err) {
          console.error('[CrmActivity] Failed to increment hours:', err);
        }
      }

      // skipSessionWrite is ignored for the unified recorder (always write).
      void skipRef.current;

      const ctx = contextRef.current;
      let attributedTo: CrmActivityAttributedTo = 'none';
      if (projectActiveRef.current) attributedTo = 'project';
      else if (leadActiveRef.current) attributedTo = 'lead';
      else if (ctx.entityType === 'project') attributedTo = 'project';
      else if (ctx.entityType === 'lead' || ctx.entityType === 'deal' || ctx.entityType === 'proposal') {
        attributedTo = 'lead';
      }

      try {
        await saveCrmActivitySession(
          {
            userId: uid,
            userName: userNameRef.current || 'Unknown',
            userEmail: userEmailRef.current,
            type: 'implied',
            minutes: activeMinutes,
            presenceMinutes,
            activeSeconds,
            presenceSeconds,
            startedAt: start,
            endedAt: end,
            path: ctx.path,
            pageKey: ctx.pageKey,
            pageLabel: ctx.pageLabel,
            activityClass: ctx.activityClass,
            jobId: ctx.jobId,
            dealId: ctx.dealId,
            entityType: ctx.entityType,
            entityId: ctx.entityId,
            entityName: ctx.entityName,
            createdAt: new Date(),
            moduleKey: ctx.moduleKey,
            moduleLabel: ctx.moduleLabel,
            viewKey: ctx.viewKey || ctx.moduleKey,
            viewLabel: ctx.viewLabel || ctx.moduleLabel,
            search: ctx.search,
            tabId: getCrmActivityTabId(),
            sessionId: getCrmActivitySessionId(),
            attributedTo,
            projectId: attributedTo === 'project'
              ? (ctx.jobId || ctx.entityId)
              : undefined,
            leadId: attributedTo === 'lead'
              ? ctx.entityId
              : undefined,
            focused: isWindowFocused(),
            visible: typeof document === 'undefined' ? true : !document.hidden,
          },
          { queueOnFailure: true },
        );
      } catch (err) {
        console.error('[CrmActivity] Failed to save session:', err);
      }
    },
  });

  useEffect(() => {
    contextRef.current = context;
    skipRef.current = skipSessionWrite;
    projectActiveRef.current = projectTrackingActive;
    leadActiveRef.current = leadTrackingActive;
  }, [context, skipSessionWrite, projectTrackingActive, leadTrackingActive]);
}
